import * as React from 'react'
import { Form, Input, Tabs, Spin } from 'antd'
import { set, get, isEmpty } from 'lodash'
import produce from 'immer'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import CheckboxList from 'elements/CheckboxList'
import Checkbox from 'elements/Checkbox'
import DatePicker from 'elements/DatePicker'
import ScheduleExceptions from 'containers/Operators/ScheduleExceptions'
import { Row, Col } from 'elements/Grid'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  getChangedItems,
  validateBarcode,
  isReadOnly,
} from 'helpers/formViews'
import { HARD_SPACE } from 'helpers/utils'
import { getUserPermission } from 'helpers/auth'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import IssueLimitLists from 'containers/Operators/IssueLimitLists'
import FormButtons from 'elements/FormButtons'

class FormView extends React.Component {
  state = { scheduleExceptionItems: [] }

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.operatorId,
      this.props.linkTargetRecord?.jobTaskSettings?.assignedOperatorId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                name: this.props.name,
                email: this.props.email,
                userName: this.props.userName,
                operatorGroups: [],
                active: true,
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getOperatorGroups(),
        this.props.getUsers(),
      ])

      const [scheduleExceptionItems, operatorIssueLimitLists] = await Promise.all([
        itemId ? this.props.getOperatorScheduleExceptionsItems(itemId) : Promise.resolve(null),
        itemId ? this.props.getOperatorIssueLimitLists({ operatorId: itemId }) : Promise.resolve(null),
      ])

      this.setState({
        item: get(responses[0], 'value.data', {}),
        fieldSettings: get(responses[1], 'value.data.fieldSettings', []),
        operatorGroups: get(responses[2], 'value.data.items', []),
        users: get(responses[3], 'value.data.items', []),
        scheduleExceptionItems: get(scheduleExceptionItems, 'value.data.items', []),
        scheduleExceptionItemsOriginal: get(scheduleExceptionItems, 'value.data.items', []),
        scheduleExceptionItemsTotals: get(scheduleExceptionItems, 'value.data.pageTotals', {}),
        operatorIssueLimitLists: get(operatorIssueLimitLists, 'value.data.items', []),
        operatorIssueLimitListsOriginal: get(operatorIssueLimitLists, 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  saveScheduleExceptionItems = async (operatorId) => {
    const { creating, updating, deleting } = getChangedItems(
      this.state.scheduleExceptionItemsOriginal,
      this.state.scheduleExceptionItems
    )

    if (!isEmpty(deleting)) {
      await this.props.deleteOperatorScheduleExceptionsItems(
        operatorId,
        deleting.map((each) => each.id)
      )
    }

    if (!isEmpty(updating)) {
      await this.props.updateOperatorScheduleExceptionsItems(
        operatorId,
        updating.map((each) => ({ ...each, operatorId }))
      )
    }

    if (!isEmpty(creating)) {
      await this.props.createOperatorScheduleExceptionsItems(
        operatorId,
        creating.map(({ id, operatorScheduleExceptionId, ...rest }) => ({
          ...rest,
          operatorId,
        }))
      )
    }
  }

  saveOperatorIssueLimitLists = async (operatorId) => {
    const { creating, deleting } = getChangedItems(
      this.state.operatorIssueLimitListsOriginal,
      this.state.operatorIssueLimitLists
    )

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteOperatorIssueLimitLists(deleting.map((each) => each.id))

      showClientNotifications({ response })
    }

    if (!isEmpty(creating)) {
      const response = await this.props.createOperatorIssueLimitLists(
        creating.map(({ issueLimitListId }) => ({
          issueLimitListId,
          operatorId,
        }))
      )

      showClientNotifications({ response })
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await Promise.all([
      this.saveScheduleExceptionItems(saved.value.data.id),
      this.saveOperatorIssueLimitLists(saved.value.data.id),
    ])

    return saved
  }

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || getUserPermission('Operators') !== 'Edit'

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Row>
            <Col xs={24} md={8}>
              <Form.Item label={createLabel('barcode')}>
                {createFieldDecorator('barcode', { extraRules: [validateBarcode()] })(
                  <Input readOnly={readOnly} autoFocus />
                )}
              </Form.Item>
              <Form.Item label={createLabel('name')}>
                {createFieldDecorator('name')(<Input readOnly={readOnly} />)}
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={createLabel('number')}>
                {createFieldDecorator('number')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('email')}>
                {createFieldDecorator('email')(<Input type="email" readOnly={readOnly} />)}
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={createLabel('userName')}>
                {createFieldDecorator('userName')(
                  <Select placeholder={t('none')} readOnly={readOnly}>
                    {(this.state.users ?? []).map((each) => (
                      <Option key={each.userName} value={each.userName}>
                        <span {...getOptionProps(each)}>{each.displayName}</span>
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
              <Col xs={24} md={12}>
                <Form.Item label={HARD_SPACE}>
                  {createFieldDecorator('isCertified', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('isCertified')}</Checkbox>
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={HARD_SPACE}>
                  {createFieldDecorator('active', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                  )}
                </Form.Item>
              </Col>
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={16}>
              <Form.Item label={createLabel('description')}>
                {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={createLabel('certificationExpiryDate')}>
                {createFieldDecorator('certificationExpiryDate')(
                  <DatePicker disabled={!this.props.form.getFieldValue('isCertified')} readOnly={readOnly} />
                )}
              </Form.Item>
            </Col>
          </Row>
          <Tabs
            activeKey={this.state.tabsActiveKey || 'operatorGroups'}
            onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
          >
            <Tabs.TabPane key="operatorGroups" tab={t('groups')} forceRender>
              <Row>
                <Col xs={24} md={12}>
                  <Form.Item label={t('memberOf')}>
                    <CheckboxList
                      dataSource={this.state.operatorGroups}
                      value={item.operatorGroupIds}
                      onChange={(values) => this.setItemValue('operatorGroupIds', values)}
                      readOnly={readOnly}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="operatorSchedule" tab={t('schedule')} forceRender>
              <Row>
                {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((each) => (
                  <Col key={each} xs={24} md={3}>
                    <Form.Item label={createLabel(each)}>
                      {createFieldDecorator(each)(
                        <InputNumber placeholder="0.0" precision={1} min={0} max={24} step={0.5} readOnly={readOnly} />
                      )}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Row>
                <Col>
                  {!!item && (
                    <ScheduleExceptions
                      items={this.state.scheduleExceptionItems ?? []}
                      pageTotals={this.state.scheduleExceptionItemsTotals ?? {}}
                      onChange={(values) => this.setState({ scheduleExceptionItems: values })}
                      readOnly={readOnly}
                    />
                  )}
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="issueLimitLists" tab={t('issueLimits')} forceRender>
              <IssueLimitLists
                settingsType="issueLimitLists"
                items={this.state.operatorIssueLimitLists ?? []}
                onChange={(values) => this.setState({ operatorIssueLimitLists: values })}
                parentRecord={item}
                readOnly={readOnly}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
        <div className="form-buttons-container">
          <FormButtons
            readOnly={readOnly}
            onCancel={this.props.onCancel}
            onSubmit={this.handleSubmit}
            saveButtonLoading={this.state.saveButtonLoading}
            saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
          />
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
